/**
 * Project overall-progress bar fill colors by completion band (product UX).
 * @see table: 0–24% gray, 25–69% blue, 70–99% orange, 100% green.
 */
export const PROJECT_PROGRESS_FILL_COLORS = {
  low: '#D1D5DB',
  active: '#2563EB',
  final: '#F59E0B',
  complete: '#10B981',
};

/** Full legend (e.g. help docs); tooltips use only the active band via `getProjectProgressTooltipTitle`. */
export const PROJECT_PROGRESS_COLOR_GUIDE =
  'Gray 0–24%: not started or early phase. Blue 25–69%: active, in progress. Orange 70–99%: nearing completion (final push). Green: all tasks closed (100%).';

/**
 * @param {number} closedCount
 * @param {number} totalCount
 * @param {number} [progressPct] - Display percentage 0–100 (should match UI rounding).
 */
function resolveProjectProgressContext(closedCount, totalCount, progressPct) {
  const t = Number(totalCount) || 0;
  const c = Number(closedCount) || 0;
  const pct =
    progressPct != null && Number.isFinite(Number(progressPct))
      ? Math.max(0, Math.min(100, Math.round(Number(progressPct))))
      : t > 0
        ? Math.max(0, Math.min(100, Math.round((c / t) * 100)))
        : 0;
  const isComplete = t > 0 && c >= t;
  return { t, c, pct, isComplete };
}

/**
 * @param {number} closedCount
 * @param {number} totalCount
 * @param {number} [progressPct] - Display percentage 0–100 (should match UI rounding).
 */
export function getProjectProgressFillColor(closedCount, totalCount, progressPct) {
  const { t, pct, isComplete } = resolveProjectProgressContext(closedCount, totalCount, progressPct);
  if (isComplete) return PROJECT_PROGRESS_FILL_COLORS.complete;
  if (t <= 0 || pct <= 24) return PROJECT_PROGRESS_FILL_COLORS.low;
  if (pct <= 69) return PROJECT_PROGRESS_FILL_COLORS.active;
  return PROJECT_PROGRESS_FILL_COLORS.final;
}

/**
 * Native `title` for the progress bar: progress summary + meaning of the **current** fill color only.
 * @param {number} closedCount
 * @param {number} totalCount
 * @param {number} [progressPct]
 */
export function getProjectProgressTooltipTitle(closedCount, totalCount, progressPct) {
  const { t, c, pct, isComplete } = resolveProjectProgressContext(closedCount, totalCount, progressPct);

  if (t <= 0) {
    return 'No tasks yet — add tasks to see overall progress.';
  }

  let bandMeaning;
  if (isComplete) {
    bandMeaning = 'Green: all tasks closed (100%).';
  } else if (pct <= 24) {
    bandMeaning = 'Gray: not started or early phase (0–24% complete).';
  } else if (pct <= 69) {
    bandMeaning = 'Blue: active and in progress (25–69% complete).';
  } else {
    bandMeaning = 'Orange: nearing completion / final push (70–99% complete).';
  }

  return `Overall progress: ${pct}% (${c} of ${t} tasks closed). ${bandMeaning}`;
}
